import { useState, useEffect, useCallback } from "react";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Avatar from "@mui/material/Avatar";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSnackbar } from "notistack";

import { useUserContext } from "../../contexts/UserContext";
import { useTranslationsContext } from "../../contexts/TranslationsContext";
import { updateUser, updatePassword } from "../../api/users";
import { useRestMutation } from "../../hooks/useRestMutation";
import { UpdateUserPayload, UpdatePasswordPayload, UserResponse } from "../../api/types";
import { variantSuccess, variantError } from "../../constants/constants";
import { Id } from "../../types";

import "./UserSettingsDialog.css";

type UserSettingsDialogProps = {
  open: boolean;
  onClose(): void;
};

export default function UserSettingsDialog({ open, onClose }: UserSettingsDialogProps) {
  const userContext = useUserContext();
  const { translation } = useTranslationsContext();
  const { enqueueSnackbar } = useSnackbar();

  // Profile fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [bio, setBio] = useState("");

  // Password fields
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Mutations
  const updateUserFn = useCallback(
    (payload: UpdateUserPayload) => updateUser(userContext.id as Id, payload),
    [userContext.id]
  );
  const updatePasswordFn = useCallback(
    (payload: UpdatePasswordPayload) => updatePassword(userContext.id as Id, payload),
    [userContext.id]
  );
  const [triggerUpdateUser, { loading: profileLoading }] =
    useRestMutation<UserResponse, UpdateUserPayload>(updateUserFn);
  const [triggerUpdatePassword, { loading: passwordLoading }] =
    useRestMutation<UserResponse, UpdatePasswordPayload>(updatePasswordFn);

  const loading = profileLoading || passwordLoading;

  // Sync form from context when dialog opens
  useEffect(() => {
    if (open) {
      setFirstName(userContext.firstName || "");
      setLastName(userContext.lastName || "");
      setJobTitle(userContext.jobTitle || "");
      setBio(userContext.bio || "");
      setPasswordOpen(false);
      setCurrentPassword("");
      setNewPassword("");
    }
  }, [open, userContext.firstName, userContext.lastName, userContext.jobTitle, userContext.bio]);

  const profileDirty =
    firstName !== (userContext.firstName || "") ||
    lastName !== (userContext.lastName || "") ||
    jobTitle !== (userContext.jobTitle || "") ||
    bio !== (userContext.bio || "");

  const passwordDirty = currentPassword.length > 0 && newPassword.length >= 6;

  async function handleSave() {
    let profileOk = true;
    let passwordOk = true;

    // Save profile if changed
    if (profileDirty) {
      const result = await triggerUpdateUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        jobTitle: jobTitle.trim() || undefined,
        bio: bio.trim() || undefined,
      });
      if (result) {
        userContext.updateProfile({
          firstName: result.firstName,
          lastName: result.lastName,
          jobTitle: result.jobTitle,
          bio: result.bio,
        });
        enqueueSnackbar(translation.UPDATE_PROFILE_SUCCESS?.defaultMessage || "Profile updated", {
          variant: variantSuccess,
        });
      } else {
        profileOk = false;
        enqueueSnackbar(translation.UPDATE_PROFILE_ERROR?.defaultMessage || "Error updating profile", {
          variant: variantError,
        });
      }
    }

    // Save password if filled
    if (passwordDirty) {
      const result = await triggerUpdatePassword({
        currentPassword,
        newPassword,
      });
      if (result) {
        enqueueSnackbar(translation.UPDATE_PASSWORD_SUCCESS?.defaultMessage || "Password updated", {
          variant: variantSuccess,
        });
        setCurrentPassword("");
        setNewPassword("");
        setPasswordOpen(false);
      } else {
        passwordOk = false;
        enqueueSnackbar(
          translation.CURRENT_PASSWORD_INCORRECT?.defaultMessage || "Current password is incorrect",
          { variant: variantError }
        );
      }
    }

    if (profileOk && passwordOk) {
      onClose();
    }
  }

  const canSave = (profileDirty || passwordDirty) && !loading && firstName.trim().length > 0;

  const displayName =
    userContext.firstName || userContext.email?.split("@")[0] || "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: "85vh" } }}
    >
      {/* Header: avatar + identity */}
      <div className="us-header">
        <Avatar className="us-avatar">
          {(userContext.firstName || userContext.email || "?")
            .charAt(0)
            .toUpperCase()}
        </Avatar>
        <div className="us-identity">
          <Tooltip title={displayName}>
            <span className="us-identity-name">{displayName}</span>
          </Tooltip>
          <Tooltip title={userContext.email}>
            <span className="us-identity-email">{userContext.email}</span>
          </Tooltip>
        </div>
      </div>

      <div className="us-section-divider" />

      <DialogContent sx={{ overflow: "auto", pt: 2.5, pb: 1 }}>
        {/* Profile fields */}
        <div className="us-form">
          <TextField
            label={translation.FIRST_NAME?.defaultMessage || "First name"}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            size="small"
            required
            error={firstName.trim().length === 0}
          />
          <TextField
            label={translation.LAST_NAME?.defaultMessage || "Last name"}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            size="small"
          />
          <TextField
            label={translation.JOB_TITLE?.defaultMessage || "Job title"}
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            size="small"
            placeholder="e.g. Lehrer, Professor"
            className="us-form-full"
          />
          <TextField
            label={translation.BIO?.defaultMessage || "Bio"}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            className="us-form-full"
          />
        </div>

        <div className="us-section-divider" style={{ margin: "20px 0 16px" }} />

        {/* Password toggle */}
        <div
          className="us-password-toggle"
          onClick={() => setPasswordOpen(!passwordOpen)}
          style={{ padding: 0 }}
        >
          <KeyboardArrowDownIcon
            className="us-password-toggle-icon"
            data-open={passwordOpen ? "true" : "false"}
            sx={{ fontSize: 20 }}
          />
          <span className="us-password-toggle-label">
            {translation.CHANGE_PASSWORD?.defaultMessage || "Change password"}
          </span>
        </div>

        <Collapse in={passwordOpen} timeout={250}>
          <div className="us-password-form" style={{ padding: "12px 0 0" }}>
            <TextField
              label={translation.CURRENT_PASSWORD?.defaultMessage || "Current password"}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              size="small"
              autoComplete="current-password"
            />
            <TextField
              label={translation.NEW_PASSWORD?.defaultMessage || "New password"}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              size="small"
              autoComplete="new-password"
              helperText={
                newPassword.length > 0 && newPassword.length < 6
                  ? translation.PASSWORD_MIN_LENGTH?.defaultMessage || "Min 6 characters"
                  : undefined
              }
              error={newPassword.length > 0 && newPassword.length < 6}
            />
          </div>
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {translation.CANCEL?.defaultMessage || "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {translation.SAVE?.defaultMessage || "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
